import { Component, OnInit, inject, viewChild } from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { take } from 'rxjs/operators';

import { AppleProfilePresetType, AppleProfilePresetViewActions, selectProfileTypeFromRoute } from '@era/stores';
import { Store } from '@ngrx/store';
import { PaneComponent, slugify } from '@rocksoft/cdk';
import {
  AppleAirPlaySecurityPresetAdd,
  AppleRestrictionsProfilePresetAdd,
  AppleWifiProfilePreset,
  AppleWifiProfilePresetAdd,
} from '@rocksoft/mdm-sdk-v2';
import { PayloadContent } from '../../../../viewmodels/apple/profiles/payload-content.model';
import { RestrictionsSettingsComponent } from '../../../apple-profiles/profile-components/restrictions-profile/restrictions-settings/restrictions-settings.component';
import { WifiProfileComponent } from '../../../apple-profiles/profile-components/wifi-profile/wifi-profile.component';
import { AirplayComponent } from '../../airplay-component/airplay.component';
import { isValidProfileType } from '../profile-preset-type-guard';

@Component({
  selector: 'ml-mdm-settings-profile-preset-add',
  templateUrl: './settings-profile-preset-add.container.html',
  imports: [PaneComponent, ReactiveFormsModule, AirplayComponent, RestrictionsSettingsComponent, WifiProfileComponent],
})
export class SettingsProfilePresetAddContainerComponent implements OnInit {
  private readonly store = inject(Store);
  private readonly formBuilder = inject(UntypedFormBuilder);

  profileType: AppleProfilePresetType;

  private readonly airplayProfileComponent = viewChild(AirplayComponent);
  private readonly restrictionsProfileComponent = viewChild(RestrictionsSettingsComponent);
  private readonly wifiProfileComponent = viewChild(WifiProfileComponent);

  airplayPreset: AppleAirPlaySecurityPresetAdd = {
    id: '',
    payloadDescription: 'The AirPlay Security payload locks the Apple TV to a particular style of AirPlay Security.',
    payloadDisplayName: 'AirPlay Security Payload',
    payloadIdentifier: 'com.methylium.airplay.test',
    payloadOrganization: 'Methylium B.V.',
    payloadVersion: 1,
    payloadType: 'com.apple.airplay.security',
  };

  restrictionsProfile?: AppleRestrictionsProfilePresetAdd;

  private readonly payloadContent: PayloadContent = {
    payloadDescription: 'Restrictions profile configures the restrictions the device has.',
    payloadDisplayName: 'Restrictions Profile',
    payloadIdentifier: 'com.methylium.restrictions.test',
    payloadOrganization: 'Methylium B.V.',
    payloadVersion: 1,
    payloadType: 'com.apple.applicationaccess',
    payloadUUID: '3da6b0ec-e6d2-4ba6-94a6-a98e041ab9af',
  };

  // Preset data - uniqueId is required by SDK but server-generated on create.
  wifiProfile: AppleWifiProfilePresetAdd = {
    id: '',
    payloadDescription: 'Wi-Fi profile configures the device to use the specified Wi-Fi network.',
    payloadDisplayName: 'Wi-Fi Profile',
    payloadIdentifier: 'com.methylium.wifi.test',
    payloadOrganization: 'Methylium B.V.',
    payloadVersion: 1,
    payloadType: 'com.apple.wifi.managed',
    disableAssociationMACRandomization: true,
  };

  readonly form: UntypedFormGroup;

  constructor() {
    this.form = this.formBuilder.group({
      id: ['', Validators.required],
    });
  }

  ngOnInit(): void {
    this.store
      .select(selectProfileTypeFromRoute)
      .pipe(take(1))
      .subscribe((profileType) => {
        if (!profileType || !isValidProfileType(profileType)) {
          throw new Error('profileType is required in route data and must be a valid AppleProfilePresetType');
        }
        this.profileType = profileType;
      });
  }

  get showIdForm(): boolean {
    return this.profileType === 'airplay-security' || this.profileType === 'restrictions-profile';
  }

  get headerTitle(): string {
    switch (this.profileType) {
      case 'airplay-security':
        return 'Add Airplay Preset';
      case 'restrictions-profile':
        return 'Add Restrictions Preset';
      case 'wifi-profile':
        return 'Add WiFi Preset';
      default:
        return 'Add Preset';
    }
  }

  savingPreset(updatedProfile?: AppleWifiProfilePreset) {
    switch (this.profileType) {
      case 'airplay-security':
        this.savingAirplayPreset();
        break;
      case 'restrictions-profile':
        this.savingRestrictionsPreset();
        break;
      case 'wifi-profile':
        this.savingWifiPreset(updatedProfile);
        break;
    }
  }

  private savingAirplayPreset() {
    if (!this.airplayProfileComponent()?.doneEditing()) {
      return;
    }
    this.airplayPreset.id = slugify(this.form.get('id')?.value);
    this.store.dispatch(
      AppleProfilePresetViewActions.presetAdded({
        profileType: 'airplay-security',
        preset: this.airplayPreset,
      })
    );
  }

  private savingRestrictionsPreset() {
    if (!this.restrictionsProfileComponent()?.doneEditing() || !this.form.valid) {
      return;
    }

    const id = slugify(this.form.get('id')?.value);
    // Preset data - uniqueId is required by SDK but server-generated on create.
    const preset: AppleRestrictionsProfilePresetAdd = Object.assign(
      {},
      this.payloadContent,
      this.restrictionsProfile || {},
      { id }
    );

    this.store.dispatch(AppleProfilePresetViewActions.presetAdded({ profileType: 'restrictions-profile', preset }));
  }

  private savingWifiPreset(updatedProfile?: AppleWifiProfilePreset) {
    // Ensure form is valid before saving
    const wifiProfileComponent = this.wifiProfileComponent();
    const formComponent = wifiProfileComponent?.formComponent();
    if (formComponent?.form && !formComponent.form.valid) {
      return;
    }

    // Use the updated profile from the event if provided, otherwise fall back to this.wifiProfile
    const profileToSave = updatedProfile || this.wifiProfile;
    if (!profileToSave.SSID_STR) {
      return;
    }
    profileToSave.id = slugify(profileToSave.SSID_STR);

    this.store.dispatch(
      AppleProfilePresetViewActions.presetAdded({ profileType: 'wifi-profile', preset: profileToSave })
    );
  }
}
